import { browserAccountStateSessionRepository } from "../account/account-state-session-repository.js";
import { createLegalAcceptanceRepository } from "../account/legal-acceptance-repository.js";

export const LEGAL_ACCEPTANCE_NOTICE_TAG = "fishy-legal-acceptance-notice";

const HTMLElementBase = globalThis.HTMLElement ?? class {};

export class FishyLegalAcceptanceNotice extends HTMLElementBase {
  constructor() {
    super();
    this._connected = false;
    this._selectedAccountId = null;
    this._request = null;
    this._unsubscribe = null;
    this._sessionRepositoryProvider = browserAccountStateSessionRepository;
    this._legalRepositoryFactory = createLegalAcceptanceRepository;
  }

  connectedCallback() {
    this._connected = true;
    this.hidden = true;
    this.setAttribute?.("data-legal-acceptance-notice-state", "loading");

    try {
      this._legalRepository = this._legalRepositoryFactory({
        resolveApiUrl: globalThis.window?.__fishystuffResolveApiUrl,
      });
      const sessionRepository = this._sessionRepositoryProvider();
      this._unsubscribe = sessionRepository.subscribe((state) => {
        this._projectSessionState(state);
      });
      Promise.resolve(sessionRepository.load()).catch(() => {
        if (this._connected) this._showUnavailable();
      });
    } catch (_error) {
      this._showUnavailable();
    }
  }

  disconnectedCallback() {
    this._connected = false;
    this._selectedAccountId = null;
    this._request?.abort();
    this._request = null;
    this._unsubscribe?.();
    this._unsubscribe = null;
  }

  _setState(state, visible) {
    this.hidden = !visible;
    this.setAttribute?.("data-legal-acceptance-notice-state", state);
  }

  _showUnavailable() {
    if (!this._connected || !this._selectedAccountId) return;
    this._setState("unavailable", true);
  }

  _projectSessionState(state) {
    if (state?.kind !== "ready") {
      this._selectedAccountId = null;
      this._request?.abort();
      this._request = null;
      this._setState(state?.kind === "signedOut" ? "signed-out" : "loading", false);
      return;
    }
    if (state.accountId === this._selectedAccountId) return;
    this._selectedAccountId = state.accountId;
    void this._loadStatus(state.accountId);
  }

  async _loadStatus(accountId) {
    this._request?.abort();
    const controller = new AbortController();
    this._request = controller;
    this._setState("loading", false);
    const locale = globalThis.document?.documentElement?.lang === "de-DE" ? "de-DE" : "en-US";
    try {
      const status = await this._legalRepository.status(locale, {
        signal: controller.signal,
      });
      if (
        !this._connected
        || controller.signal.aborted
        || this._selectedAccountId !== accountId
      ) return;
      this._setState(status.accepted ? "accepted" : "required", !status.accepted);
    } catch (_error) {
      if (!controller.signal.aborted) this._showUnavailable();
    } finally {
      if (this._request === controller) this._request = null;
    }
  }

  markAccepted(accountId) {
    if (accountId !== this._selectedAccountId) return;
    this._request?.abort();
    this._request = null;
    this._setState("accepted", false);
  }
}

export function registerLegalAcceptanceNotice() {
  if (
    typeof globalThis.customElements !== "undefined"
    && !globalThis.customElements.get(LEGAL_ACCEPTANCE_NOTICE_TAG)
  ) {
    globalThis.customElements.define(
      LEGAL_ACCEPTANCE_NOTICE_TAG,
      FishyLegalAcceptanceNotice,
    );
  }
}
